import { SystemStyleObject } from '@betterment/samba-styled-system/types';

import { CreateBulkFeeIntentMutation } from './graphql/mutation.createBulkFeeIntent.generated';
import { BulkFeeIntentsQuery } from './graphql/query.bulk_fee_intents.generated';
import { BulkFeeAttemptQuery } from './graphql/query.bulkFeeAttempt.generated';

import { FeeAttemptBatch, FeeAttemptFailureReason } from '../../../graphql/schema';

export interface BulkFeeIntentsColumns {
  name: string;
  css?: SystemStyleObject;
}

export type BulkFeeIntent = BulkFeeIntentsQuery['firm']['bulkFeeIntents'][number];

export type BulkFeeIntentProps = {
  bulkFeeIntent: BulkFeeIntent;
};

export type BulkFeeIntentBodyCellProps = React.PropsWithChildren<{
  noWrap?: boolean;
  pr?: boolean;
}>;
export type BulkFeeAttemptResponse = {
  id: string;
};

export type BulkFeeAttemptErrorResponse = {
  errors: {
    csv_file?: string[];
    name?: string[];
  };
};

export type CreateBulkFeeAttemptParams = {
  csvFile: File;
  name: string;
};

export type CreateBulkFeeAttemptResult =
  | {
      success: true;
      data: { id: string };
    }
  | {
      success: false;
      errors: {
        csvFile?: string[];
        name?: string[];
      };
    };

export type UploadCsvFormValues = {
  name: string;
  csvFile: File | null;
  useUploadedCsvName: boolean;
};

export type BulkFeeAttempt = BulkFeeAttemptQuery['firm']['bulkFeeAttempt'];

export type FeeAttemptInputAccount = FeeAttemptFailure['account'];
export type FeeAttemptInputAmount = FeeAttemptFailure['amount'];

export type FeeAttemptsResult = NonNullable<BulkFeeAttemptQuery['firm']['bulkFeeAttempt']>['feeAttempts'];

type InvalidFeeAttemptBatch = Extract<
  NonNullable<BulkFeeAttemptQuery['firm']['bulkFeeAttempt']>['feeAttempts'],
  { __typename: 'InvalidFeeAttemptBatch' }
>;

export type FeeAttemptFailure = InvalidFeeAttemptBatch['failures'][number];

export function isInvalidFeeAttemptBatch(result: FeeAttemptsResult): result is InvalidFeeAttemptBatch {
  return result.__typename === 'InvalidFeeAttemptBatch';
}

export function isValidFeeAttemptBatch(result: FeeAttemptsResult): result is FeeAttemptBatch {
  return result.__typename === 'FeeAttemptBatch';
}

export const ERROR_MESSAGES: Record<FeeAttemptFailureReason, string> = {
  [FeeAttemptFailureReason.AccountClosed]: 'Account is closed and cannot be charged fees',
  [FeeAttemptFailureReason.AccountNotFound]: 'Invalid account number',
  [FeeAttemptFailureReason.ExceedsMaxFeeAmount]: 'Exceeds fee maximum',
  [FeeAttemptFailureReason.IneligibleAccount]: 'Ineligible account for fees',
  [FeeAttemptFailureReason.InsufficientBalance]: 'Insufficient balance',
  [FeeAttemptFailureReason.InsufficientBalanceCombined]: 'Combined fees are greater than the billable balance',
  [FeeAttemptFailureReason.InvalidAmount]: 'Invalid fee amount',
  [FeeAttemptFailureReason.NonPositiveAmount]: 'Must be greater than zero',
  [FeeAttemptFailureReason.DescriptionTooLong]: 'Description exceeds 250 character limit',
};

export type FeeAttempt = {
  id: string;
  owner: string;
  accountName: string;
  legalAccountType: string;
  amount: number;
  description: string;
};

export enum UploadCsvValidationErrorType {
  MissingHeaders = 'missing_headers',
  TooManyRows = 'too_many_rows',
  InvalidFileFormat = 'invalid_file_format',
  TooManyContentFailures = 'too_many_content_failures',
  NoFeeAmounts = 'no_fee_amounts',
  GenericError = 'generic_error',
}

const CSV_ERROR_PATTERNS: Array<{
  substring: string;
  type: UploadCsvValidationErrorType;
}> = [
  {
    substring: 'missing required header',
    type: UploadCsvValidationErrorType.MissingHeaders,
  },
  {
    substring: 'csv exceeds maximum row limit',
    type: UploadCsvValidationErrorType.TooManyRows,
  },
  {
    substring: 'invalid csv format',
    type: UploadCsvValidationErrorType.InvalidFileFormat,
  },
  {
    substring: 'error reading csv',
    type: UploadCsvValidationErrorType.InvalidFileFormat,
  },
  {
    substring: 'csv contains too many errors',
    type: UploadCsvValidationErrorType.TooManyContentFailures,
  },
  {
    substring: 'must contain at least one fee amount',
    type: UploadCsvValidationErrorType.NoFeeAmounts,
  },
];

export const parseUploadCsvValidationError = (errorMessages: string[]): UploadCsvValidationErrorType | null => {
  const [firstError] = errorMessages;

  if (!firstError) {
    return null;
  }

  const normalized = firstError.toLowerCase();

  const matchedPattern = CSV_ERROR_PATTERNS.find(pattern => {
    return normalized.includes(pattern.substring);
  });

  if (matchedPattern) {
    return matchedPattern.type;
  }

  return null;
};

type CreateBulkFeeIntentResponse = NonNullable<CreateBulkFeeIntentMutation['createBulkFeeIntent']>;

type CreateBulkFeeIntentSuccess = CreateBulkFeeIntentResponse & {
  displayableErrors: readonly [];
  result: NonNullable<CreateBulkFeeIntentResponse['result']>;
};

export function isSuccessResponse(response: CreateBulkFeeIntentResponse): response is CreateBulkFeeIntentSuccess {
  return response.displayableErrors.length === 0 && response.result !== null;
}

const combineAndSortFailuresByRow = (
  groupedFailures: [string, FeeAttemptFailure[]][],
  ungroupedFailures: FeeAttemptFailure[]
): SortedFailure[] => {
  return [
    ...groupedFailures.map(([accountKey, failures]) => ({
      type: 'grouped' as const,
      accountKey,
      failures,
      minRow: Math.min(...failures.map(f => f.row)),
    })),
    ...ungroupedFailures.map(failure => ({
      type: 'ungrouped' as const,
      failure,
      minRow: failure.row,
    })),
  ].sort((a, b) => a.minRow - b.minRow);
};

export type SortedFailure =
  | { type: 'grouped'; accountKey: string; failures: FeeAttemptFailure[]; minRow: number }
  | { type: 'ungrouped'; failure: FeeAttemptFailure; minRow: number };

/**
 * Separates, groups, and sorts fee attempt failures for display.
 * InsufficientBalanceCombined errors are grouped by account ID.
 * All other errors remain ungrouped.
 * Results are sorted by row number (earliest row for grouped failures).
 */
export function separateAndGroupFailures(failures: FeeAttemptFailure[]): SortedFailure[] {
  const insufficientBalanceCombined = failures.filter(failure =>
    failure.failureReasons.includes(FeeAttemptFailureReason.InsufficientBalanceCombined)
  );

  const others = failures.filter(
    failure => !failure.failureReasons.includes(FeeAttemptFailureReason.InsufficientBalanceCombined)
  );

  const grouped = insufficientBalanceCombined.reduce(
    (acc, failure) => {
      const accountKey =
        failure.account.__typename === 'InvalidAccountString' ? failure.account.input : failure.account.id;

      if (!acc[accountKey]) {
        acc[accountKey] = [];
      }
      acc[accountKey].push(failure);
      return acc;
    },
    {} as Record<string, FeeAttemptFailure[]>
  );

  return combineAndSortFailuresByRow(Object.entries(grouped), others);
}
